import React, { useState } from 'react';
import PortfolioDemo from './demos/PortfolioDemo';
import ScreenerDemo from './demos/ScreenerDemo';
import MarketTrendsDemo from './demos/MarketTrendsDemo';
import AlphaChatDemo from './demos/AlphaChatDemo';
import ResearchDemo from './demos/ResearchDemo';
import CorrelationsDemo from './demos/CorrelationsDemo';
import MacroEnergyDemo from './demos/MacroEnergyDemo';
import GradeWatchDemo from './demos/GradeWatchDemo';
import './WalkthroughTour.css';

// Interactive product tour for the logged-out marketing page — replaces the
// old YouTube walkthroughs. Each step plays one of the code-driven animated
// demos (no video). Switching steps remounts the demo (via key) so its
// animation restarts from the top.
const STEPS = [
  {
    key: 'portfolio',
    label: 'Build & Analyze',
    title: 'Build a portfolio and grade every position',
    blurb: 'Add your holdings right in the app and AlphaGrade scores each one across Quality, Setup, Convex, and Macro — with a clear read on every name.',
    Comp: PortfolioDemo,
  },
  {
    key: 'alpha',
    label: 'Chat with Alpha',
    title: 'Talk to Alpha about what you own',
    blurb: 'Ask Alpha anything about your portfolio. It researches live data — estimate revisions, institutional flow, market regime — and answers in plain English.',
    Comp: AlphaChatDemo,
  },
  {
    key: 'screener',
    label: 'Screen Stocks',
    title: 'Score any stock in seconds',
    blurb: 'Type any tickers and read their Quality, Setup, and Convex scores — then open the test-by-test breakdown behind each grade.',
    Comp: ScreenerDemo,
  },
  {
    key: 'trends',
    label: 'Market Trends',
    title: 'Read the whole market at a glance',
    blurb: 'See sector strength, the day’s movers, and the market regime so you know the backdrop before you act.',
    Comp: MarketTrendsDemo,
  },
  // Added 2026-09-30 from the walkthrough scripts in docs/video-scripts.md.
  // Appended rather than interleaved so the order returning visitors already
  // know does not shift under them; the tabs are random access anyway.
  {
    key: 'research',
    label: 'Find Ideas',
    title: 'Start from a hunch, not a ticker',
    blurb: 'Ask in plain language, get real tickers back, then screen them on the same 34 tests. A vague idea becomes a graded list in about a minute. It finds candidates, the judgment stays yours.',
    Comp: ResearchDemo,
  },
  {
    key: 'correlations',
    label: 'Correlations',
    title: 'Find out if you are actually diversified',
    blurb: 'Every holding against every other one. Names that move together are one bet wearing several tickers, and seeing that is usually the surprise.',
    Comp: CorrelationsDemo,
  },
  {
    key: 'energy',
    label: 'Macro Backdrop',
    title: 'Read the conditions behind every grade',
    blurb: 'Nine tests on the macro picture, supply, demand and the geopolitical side. Not a prediction, a read on conditions, and it feeds the conviction score on every name you look at.',
    Comp: MacroEnergyDemo,
  },
  {
    key: 'gradewatch',
    label: 'Grade Watch',
    title: 'Get told when something changes',
    blurb: 'Watch any ticker and AlphaGrade emails you when its grade moves, with the reason it moved. Nothing to check daily.',
    Comp: GradeWatchDemo,
  },
];

export default function WalkthroughTour() {
  const [active, setActive] = useState(0);
  const step = STEPS[active];
  const Demo = step.Comp;

  return (
    <div className="tour">
      <div className="tour-tabs" role="tablist">
        {STEPS.map((s, i) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={`tour-tab ${i === active ? 'active' : ''}`}
            onClick={() => setActive(i)}
          >
            <span className="tour-tab-num">{i + 1}</span>
            {s.label}
          </button>
        ))}
      </div>

      <div className="tour-body">
        <div className="tour-copy">
          <h3 className="tour-title">{step.title}</h3>
          <p className="tour-blurb">{step.blurb}</p>
          <div className="tour-nav">
            <button
              type="button"
              className="tour-navbtn"
              onClick={() => setActive((a) => Math.max(0, a - 1))}
              disabled={active === 0}
            >
              ← Back
            </button>
            <span className="tour-progress">{active + 1} / {STEPS.length}</span>
            <button
              type="button"
              className="tour-navbtn"
              onClick={() => setActive((a) => Math.min(STEPS.length - 1, a + 1))}
              disabled={active === STEPS.length - 1}
            >
              Next →
            </button>
          </div>
        </div>

        <div className="tour-demo">
          <Demo key={step.key} autoPlay loop />
        </div>
      </div>
    </div>
  );
}
